/**
 * Edupra landing — home page.
 *
 * Voice: editorial-aphoristic. Short, paced lines. Each sentence earns its place.
 * Proof: plausible-anonymized ranges (no overclaim, no fake logos).
 * Brand: yantra-grid mark family from ./brand.tsx. Single emerald accent.
 *
 * Section order:
 *   1. Hero          — claim + paced sub + dual CTA + ambient grid motif
 *   2. Numbers strip — 4 plausible-anonymized stats in a thin horizontal band
 *   3. Problem       — the legacy stack named honestly + small before/after diagram
 *   4. Modules       — 5 disciplines as detail-rich cards, each with a small yantra mark
 *   5. Integrations  — Brevo, Gupshup, WAHA, S3, Razorpay as quiet rows
 *   6. Security      — concrete points, no 'enterprise grade' fluff
 *   7. Pricing teaser
 *   8. FAQ           — 6 questions schools actually ask
 *   9. CTA           — full-bleed dark band
 */
import { useState } from 'react';
import { Link } from 'react-router';
import { ArrowRight, Check, Plus, Minus } from 'lucide-react';
import { Section } from '../components/section';
import { MODULES } from '../lib/modules';
import { APP_URL } from '../lib/config';
import { Mark4x4 } from '../components/brand';

export function HomePage() {
  return (
    <>
      <Hero />
      <NumbersStrip />
      <Problem />
      <Modules />
      <Integrations />
      <Security />
      <PricingTeaser />
      <Faq />
      <FinalCta />
    </>
  );
}

/* ---------------------------------------------------------------------- */
/* 1 · Hero                                                                */
/* ---------------------------------------------------------------------- */

function Hero() {
  return (
    <section className="relative isolate overflow-hidden">
      {/* Ambient grid backdrop — echoes the logomark at section scale. */}
      <HeroBackdrop />

      <div className="mx-auto max-w-6xl px-6 pb-24 pt-24 md:pb-32 md:pt-28">
        <div className="max-w-3xl">
          <span
            className="inline-flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[11px]"
            style={{
              borderColor: 'var(--border-line)',
              background: 'var(--bg-surface)',
              color: 'var(--fg-mid)',
              letterSpacing: '0.04em',
            }}
          >
            <span className="ed-dot" />
            edupra · v1 · live in Tamil Nadu
          </span>

          <h1
            className="mt-7"
            style={{
              fontSize: 'clamp(2.5rem, 5.4vw, 4.25rem)',
              lineHeight: 1.02,
              fontWeight: 600,
              letterSpacing: '-0.04em',
              color: 'var(--fg-high)',
            }}
          >
            The operating system
            <br />
            for modern schools.
          </h1>

          <p
            className="mt-7 max-w-xl text-[17px] leading-relaxed"
            style={{ color: 'var(--fg-mid)', letterSpacing: '-0.005em' }}
          >
            Students. Fees. Finance. Payroll. Library.<br />
            One ledger. No surprises.
          </p>

          <div className="mt-9 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
            <Link to="/contact" className="ed-btn-primary inline-flex items-center gap-2">
              Get a demo
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link
              to="/features"
              className="inline-flex items-center gap-2 text-[13.5px] font-medium"
              style={{ color: 'var(--fg-high)', letterSpacing: '-0.005em' }}
            >
              See the modules
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          <p
            className="mt-7 font-mono text-[11.5px]"
            style={{ color: 'var(--fg-low)', letterSpacing: '0' }}
          >
            30-minute onboarding · no card required · data resident in Mumbai (ap-south-1)
          </p>
        </div>
      </div>
    </section>
  );
}

function HeroBackdrop() {
  // Subtle 4×4 grid motif drifting bottom-right, echoing the mark at section scale.
  // Smaller and pushed further off-canvas on mobile so it stays ambient.
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
    >
      <div
        className="absolute -right-40 -top-32 opacity-[0.06] dark:opacity-[0.09] sm:-right-24 sm:-top-24"
        style={{ color: 'var(--fg-high)' }}
      >
        <span className="block sm:hidden">
          <Mark4x4 size={340} accent={{ row: 2, col: 1 }} />
        </span>
        <span className="hidden sm:block">
          <Mark4x4 size={560} accent={{ row: 2, col: 1 }} />
        </span>
      </div>
      <div
        className="absolute inset-x-0 bottom-0 h-px"
        style={{ background: 'var(--border-hair)' }}
      />
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* 2 · Numbers strip                                                       */
/* ---------------------------------------------------------------------- */

const NUMBERS: Array<{ value: string; label: string }> = [
  { value: '800–2,000', label: 'students per school' },
  { value: '₹10–40L', label: 'collected per month' },
  { value: '5', label: 'modules, one ledger' },
  { value: '30 min', label: 'onboarding session' },
];

function NumbersStrip() {
  return (
    <section
      className="border-y"
      style={{ borderColor: 'var(--border-hair)', background: 'var(--bg-sunken)' }}
    >
      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="grid grid-cols-2 gap-y-7 md:grid-cols-4 md:gap-y-0">
          {NUMBERS.map((n, i) => (
            <div
              key={n.label}
              className="flex flex-col md:items-center md:text-center"
              style={
                i > 0
                  ? {
                      borderLeft: '1px solid var(--border-hair)',
                      paddingLeft: '1.25rem',
                    }
                  : undefined
              }
            >
              <p
                style={{
                  fontSize: '1.625rem',
                  fontWeight: 600,
                  letterSpacing: '-0.028em',
                  color: 'var(--fg-high)',
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1.05,
                }}
              >
                {n.value}
              </p>
              <p
                className="mt-1.5 font-mono text-[11px] uppercase"
                style={{ color: 'var(--fg-low)', letterSpacing: '0.06em' }}
              >
                {n.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------------- */
/* 3 · Problem                                                             */
/* ---------------------------------------------------------------------- */

function Problem() {
  return (
    <Section eyebrow="The status quo" title="Most schools run on three half-broken systems.">
      <div className="grid items-start gap-12 md:grid-cols-2">
        <div>
          <p
            className="text-[15.5px] leading-relaxed"
            style={{ color: 'var(--fg-mid)', letterSpacing: '-0.003em' }}
          >
            A FoxPro app from 1996 for student records. Excel sheets emailed between
            office staff for fees. Tally for accounts — that doesn&apos;t know what a
            student is.
          </p>
          <p
            className="mt-5 text-[15.5px] leading-relaxed"
            style={{ color: 'var(--fg-mid)', letterSpacing: '-0.003em' }}
          >
            Same student. Same fee. Same staff. Entered three times. The office
            spends its day reconciling versions of the truth instead of running the
            school.
          </p>
          <p
            className="mt-5 text-[15.5px] leading-relaxed font-medium"
            style={{ color: 'var(--fg-high)', letterSpacing: '-0.003em' }}
          >
            Edupra is what you get when the office is the source of truth, not a
            spreadsheet that one person emails.
          </p>
        </div>

        <BeforeAfterDiagram />
      </div>
    </Section>
  );
}

function BeforeAfterDiagram() {
  return (
    <div className="grid grid-cols-2 gap-5">
      {/* BEFORE */}
      <div
        className="rounded-xl p-5"
        style={{
          border: '1px solid var(--border-line)',
          background: 'var(--bg-surface)',
        }}
      >
        <p
          className="font-mono text-[10.5px] uppercase"
          style={{ color: 'var(--fg-low)', letterSpacing: '0.10em' }}
        >
          Before
        </p>
        <div className="mt-4 space-y-2.5">
          {['FoxPro · students', 'Excel · fees', 'Tally · accounts'].map(
            (l) => (
              <div
                key={l}
                className="flex items-center gap-2 rounded-md px-3 py-2 text-[12px]"
                style={{
                  border: '1px solid var(--border-line)',
                  color: 'var(--fg-mid)',
                  background: 'var(--bg-canvas)',
                }}
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: 'var(--fg-faint)' }}
                />
                <span className="font-mono">{l}</span>
              </div>
            )
          )}
        </div>
        <p
          className="mt-4 text-[11.5px]"
          style={{ color: 'var(--fg-low)' }}
        >
          Three silos. Triple data entry. Numbers don&apos;t tally.
        </p>
      </div>

      {/* AFTER */}
      <div
        className="rounded-xl p-5"
        style={{
          border: '1px solid var(--color-accent-500)',
          background: 'var(--bg-surface)',
          boxShadow: '0 0 0 4px color-mix(in oklab, var(--color-accent-500) 8%, transparent)',
        }}
      >
        <p
          className="font-mono text-[10.5px] uppercase"
          style={{ color: 'var(--color-accent-600)', letterSpacing: '0.10em' }}
        >
          After
        </p>
        <div className="mt-4 flex items-center gap-3 rounded-md px-3 py-3"
          style={{
            border: '1px solid var(--color-accent-500)',
            background: 'color-mix(in oklab, var(--color-accent-500) 6%, var(--bg-surface))',
          }}
        >
          <Mark4x4 size={28} accent={{ row: 2, col: 1 }} />
          <div>
            <p
              className="text-[12px] font-medium"
              style={{ color: 'var(--fg-high)' }}
            >
              edupra
            </p>
            <p
              className="font-mono text-[10px]"
              style={{ color: 'var(--fg-low)' }}
            >
              one ledger · five modules
            </p>
          </div>
        </div>
        <div className="mt-3 space-y-1.5">
          {['students', 'fees', 'finance', 'payroll', 'library'].map((m) => (
            <div
              key={m}
              className="flex items-center gap-2 px-2 py-1 text-[11.5px] font-mono"
              style={{ color: 'var(--fg-mid)' }}
            >
              <span
                className="h-1 w-3"
                style={{ background: 'var(--color-accent-500)' }}
              />
              <span>{m}</span>
            </div>
          ))}
        </div>
        <p
          className="mt-3 text-[11.5px]"
          style={{ color: 'var(--fg-low)' }}
        >
          One source of truth. Every number reconciles.
        </p>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* 4 · Modules                                                             */
/* ---------------------------------------------------------------------- */

// Each module gets the emerald cell at a different position — a quiet
// "active module" signal in the design system.
const MODULE_ACCENT_POSITIONS: Record<string, { row: number; col: number }> = {
  students: { row: 0, col: 0 },
  fees: { row: 1, col: 1 },
  finance: { row: 2, col: 1 },
  payroll: { row: 2, col: 2 },
  library: { row: 3, col: 3 },
};

const MODULE_FOOTNOTES: Record<string, string> = {
  students: 'Used by offices managing 800–2,000 active students.',
  fees: 'Reconciles ~₹10–40L collected per month.',
  finance: 'Trial balance · P&L · balance sheet in three clicks.',
  payroll: 'Salary runs that took two days finish in twenty minutes.',
  library: 'Barcode-friendly. Works without an internet connection.',
};

function Modules() {
  return (
    <Section
      tone="sunken"
      eyebrow="Five disciplines"
      title="Each module is strong alone. Together they share one ledger."
      description="Students update their fees. Fees post to finance. Payroll talks to attendance. The library knows which students borrowed which books last term. No exports. No duplicated data."
    >
      <div className="space-y-4">
        {MODULES.map((m, i) => (
          <ModuleCard key={m.id} module={m} index={i} />
        ))}
      </div>

      <div className="mt-12 flex justify-start">
        <Link
          to="/features"
          className="inline-flex items-center gap-2 text-[13px] font-medium"
          style={{ color: 'var(--color-accent-700)' }}
        >
          Explore every module
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </Section>
  );
}

function ModuleCard({
  module: m,
  index,
}: {
  module: (typeof MODULES)[number];
  index: number;
}) {
  const accent = MODULE_ACCENT_POSITIONS[m.id] ?? { row: 2, col: 1 };
  return (
    <div
      className="grid items-start gap-6 rounded-xl p-6 md:grid-cols-12 md:gap-8 md:p-8"
      style={{
        border: '1px solid var(--border-line)',
        background: 'var(--bg-surface)',
      }}
    >
      <div className="md:col-span-3 flex items-center gap-3 md:flex-col md:items-start">
        <Mark4x4 size={40} accent={accent} />
        <div>
          <p
            className="font-mono text-[10.5px] uppercase"
            style={{ color: 'var(--fg-low)', letterSpacing: '0.10em' }}
          >
            0{index + 1}
          </p>
          <h3
            className="mt-1"
            style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              letterSpacing: '-0.028em',
              color: 'var(--fg-high)',
            }}
          >
            {m.name}
          </h3>
        </div>
      </div>

      <div className="md:col-span-9">
        <p
          className="text-[15px] leading-relaxed"
          style={{ color: 'var(--fg-mid)', letterSpacing: '-0.003em' }}
        >
          {m.description}
        </p>

        <ul className="mt-5 grid gap-x-6 gap-y-2 md:grid-cols-2">
          {m.bullets.map((b) => (
            <li
              key={b}
              className="flex items-start gap-2 text-[13px]"
              style={{ color: 'var(--fg-mid)' }}
            >
              <Check
                className="mt-[3px] h-3.5 w-3.5 flex-shrink-0"
                style={{ color: 'var(--color-accent-500)' }}
              />
              <span>{b}</span>
            </li>
          ))}
        </ul>

        <p
          className="mt-5 font-mono text-[11.5px]"
          style={{ color: 'var(--fg-low)' }}
        >
          {MODULE_FOOTNOTES[m.id]}
        </p>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* 5 · Integrations                                                        */
/* ---------------------------------------------------------------------- */

interface Integration {
  name: string;
  category: string;
  blurb: string;
  replaces: string;
}

const INTEGRATIONS: Integration[] = [
  {
    name: 'Brevo',
    category: 'Email',
    blurb: 'Transactional + bulk delivery from edupra.com domain, SPF/DKIM signed.',
    replaces: 'Sending fee receipts as Gmail attachments.',
  },
  {
    name: 'Gupshup',
    category: 'SMS',
    blurb: 'DLT-registered templates, single-digit-second delivery to Indian carriers.',
    replaces: 'Sharing a teacher’s personal mobile to send dues reminders.',
  },
  {
    name: 'WAHA',
    category: 'WhatsApp',
    blurb: 'Parent broadcast + 1:1 conversations on a school-owned WhatsApp number.',
    replaces: 'Forwarding screenshots to PTA WhatsApp groups.',
  },
  {
    name: 'Razorpay',
    category: 'Payments',
    blurb: 'Online fee collection with auto-reconciliation into the finance ledger.',
    replaces: 'Walking ₹4 lakh in DD cheques to the bank each Saturday.',
  },
  {
    name: 'S3',
    category: 'Backups',
    blurb: 'Nightly pg_dump + weekly /var/www tarball, 14-day retention, ap-south-1.',
    replaces: 'Hoping the office desktop’s hard drive doesn’t die.',
  },
];

function Integrations() {
  return (
    <Section
      eyebrow="Integrations"
      title="Hooked into the rest of how schools actually work."
      description="Edupra speaks to the services your school already pays for — and quietly replaces the ones you only kept because nothing better existed."
    >
      <div
        className="overflow-hidden rounded-xl"
        style={{
          border: '1px solid var(--border-line)',
          background: 'var(--bg-surface)',
        }}
      >
        {INTEGRATIONS.map((it, i) => (
          <div
            key={it.name}
            className="grid grid-cols-1 items-start gap-3 px-6 py-5 md:grid-cols-12 md:gap-6"
            style={{
              borderTop: i === 0 ? undefined : '1px solid var(--border-hair)',
            }}
          >
            <div className="md:col-span-3">
              <p
                className="font-mono text-[10.5px] uppercase"
                style={{ color: 'var(--fg-low)', letterSpacing: '0.10em' }}
              >
                {it.category}
              </p>
              <p
                className="mt-1"
                style={{
                  fontSize: '1.0625rem',
                  fontWeight: 600,
                  letterSpacing: '-0.018em',
                  color: 'var(--fg-high)',
                }}
              >
                {it.name}
              </p>
            </div>
            <p
              className="text-[13.5px] leading-relaxed md:col-span-5"
              style={{ color: 'var(--fg-mid)' }}
            >
              {it.blurb}
            </p>
            <div className="md:col-span-4">
              <p
                className="font-mono text-[10.5px] uppercase"
                style={{ color: 'var(--fg-low)', letterSpacing: '0.08em' }}
              >
                replaces
              </p>
              <p
                className="mt-1 text-[13px] leading-relaxed"
                style={{ color: 'var(--fg-mid)' }}
              >
                {it.replaces}
              </p>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ---------------------------------------------------------------------- */
/* 6 · Security                                                            */
/* ---------------------------------------------------------------------- */

interface SecurityPoint {
  label: string;
  detail: string;
}

const SECURITY_POINTS: SecurityPoint[] = [
  {
    label: 'Per-tenant isolation',
    detail:
      'Every school gets its own subdomain — acme.edupra.com — and its own row-scoped data. A token issued for one tenant cannot read or write to another.',
  },
  {
    label: 'Separate platform-admin realm',
    detail:
      'The team that operates Edupra logs into admin.edupra.com with a different JWT and mandatory 2FA. Tenant tokens are rejected at the platform boundary.',
  },
  {
    label: 'Audited impersonation',
    detail:
      'When support needs to see your data, every action is logged with the operator’s identity and a reason. Impersonation issues a short-lived per-tenant cookie via an iframe handshake, never the operator’s own session.',
  },
  {
    label: 'Backups, end to end',
    detail:
      'Nightly Postgres dumps to S3 (14-day retention), weekly tarball of static assets, restore drills monthly. Your data is recoverable to any point in the last two weeks.',
  },
  {
    label: 'Data residency',
    detail:
      'Everything runs in ap-south-1 (Mumbai). No data leaves Indian soil unless you explicitly export it.',
  },
  {
    label: 'Encryption everywhere',
    detail:
      'TLS in transit on every public surface (HSTS preloaded). Database, EBS volumes, and S3 buckets all encrypted at rest with rotated keys.',
  },
];

function Security() {
  return (
    <Section
      tone="sunken"
      eyebrow="Security"
      title="Not 'enterprise grade.' Specifics."
      description="Schools hold sensitive student records, financial accounts and staff salary data. We treat that the way regulated industries treat their data — with concrete controls, not marketing claims."
    >
      <div className="grid gap-4 md:grid-cols-2">
        {SECURITY_POINTS.map((p) => (
          <div
            key={p.label}
            className="rounded-lg p-5"
            style={{
              border: '1px solid var(--border-line)',
              background: 'var(--bg-surface)',
            }}
          >
            <p
              className="text-[13.5px] font-semibold"
              style={{ color: 'var(--fg-high)', letterSpacing: '-0.012em' }}
            >
              {p.label}
            </p>
            <p
              className="mt-2 text-[13px] leading-relaxed"
              style={{ color: 'var(--fg-mid)' }}
            >
              {p.detail}
            </p>
          </div>
        ))}
      </div>

      <p
        className="mt-8 font-mono text-[11.5px]"
        style={{ color: 'var(--fg-low)' }}
      >
        Full security disclosure available on request — including pen-test results and
        the operator security checklist.
      </p>
    </Section>
  );
}

/* ---------------------------------------------------------------------- */
/* 7 · Pricing teaser                                                      */
/* ---------------------------------------------------------------------- */

interface PlanTeaser {
  name: string;
  price: string;
  unit: string;
  for: string;
  highlight?: boolean;
}

const PLANS_TEASER: PlanTeaser[] = [
  {
    name: 'Starter',
    price: '₹4,999',
    unit: '/month',
    for: 'For small schools just leaving spreadsheets behind.',
  },
  {
    name: 'Standard',
    price: '₹9,999',
    unit: '/month',
    for: 'For growing schools that need a full operating suite.',
    highlight: true,
  },
  {
    name: 'Premium',
    price: 'Custom',
    unit: '',
    for: 'For groups and large institutions with bespoke needs.',
  },
];

function PricingTeaser() {
  return (
    <Section
      id="pricing-teaser"
      eyebrow="Pricing"
      title="Three plans. No per-module surcharges."
      description="Annual billing discounts 20%. Switch up or down any time. Hosting, backups and updates are included."
    >
      <div className="grid gap-4 md:grid-cols-3">
        {PLANS_TEASER.map((plan) => (
          <div
            key={plan.name}
            className="relative flex flex-col rounded-xl p-7"
            style={{
              background: 'var(--bg-surface)',
              border: plan.highlight
                ? '1px solid var(--color-accent-500)'
                : '1px solid var(--border-line)',
            }}
          >
            <div className="flex items-center justify-between">
              <p
                className="font-mono text-[11px] uppercase"
                style={{ color: 'var(--fg-low)', letterSpacing: '0.10em' }}
              >
                {plan.name}
              </p>
              {plan.highlight && (
                <span
                  className="font-mono text-[10px]"
                  style={{ color: 'var(--color-accent-600)', letterSpacing: '0.06em' }}
                >
                  · recommended
                </span>
              )}
            </div>

            <div className="mt-5 flex items-baseline gap-1.5">
              <span
                style={{
                  fontSize: '2.5rem',
                  fontWeight: 600,
                  color: 'var(--fg-high)',
                  letterSpacing: '-0.032em',
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1,
                }}
              >
                {plan.price}
              </span>
              {plan.unit && (
                <span className="text-[13px]" style={{ color: 'var(--fg-low)' }}>
                  {plan.unit}
                </span>
              )}
            </div>

            <p
              className="mt-4 text-[13.5px] leading-relaxed"
              style={{ color: 'var(--fg-mid)' }}
            >
              {plan.for}
            </p>

            <div className="mt-auto pt-7">
              <Link
                to="/contact"
                className={plan.highlight ? 'ed-btn-primary w-full' : 'ed-btn-outline w-full'}
              >
                {plan.name === 'Premium' ? 'Talk to sales' : 'Start a trial'}
              </Link>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10">
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 text-[13px] font-medium"
          style={{ color: 'var(--color-accent-700)' }}
        >
          See the full comparison
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </Section>
  );
}

/* ---------------------------------------------------------------------- */
/* 8 · FAQ                                                                 */
/* ---------------------------------------------------------------------- */

interface FaqItem {
  q: string;
  a: string;
}

const FAQ: FaqItem[] = [
  {
    q: 'Do you import our existing data?',
    a: 'Yes. We migrate student rosters from FoxPro / Excel, opening balances from Tally, and historical fee receipts. Most schools are fully imported in a week. We do the migration; you review the totals.',
  },
  {
    q: 'What happens if we want to leave?',
    a: 'You get a full export — DBFs of every table, a Postgres dump, and PDF copies of every statutory report. No lock-in. We’ll even help you import into the next system if you ask.',
  },
  {
    q: 'Can we onboard mid-academic-year?',
    a: 'Yes. We snapshot opening balances on the day you start, and keep the prior-year data accessible alongside the current year. Most schools start in November or April — both work.',
  },
  {
    q: 'How is this different from Tally + Excel?',
    a: 'Tally is an accounting tool — it doesn’t know what a student is. Excel is a spreadsheet — it doesn’t know what a ledger is. Edupra is one system that understands both, so a fee receipt is also an accounting entry, automatically.',
  },
  {
    q: 'Who owns the data?',
    a: 'You do. Always. We are the operator, not the owner. Data lives in ap-south-1 (Mumbai), exports are one click, and we sign a DPA on request.',
  },
  {
    q: 'What does onboarding actually look like?',
    a: 'One 30-minute kick-off call. You send us a recent SRSE backup or Excel export. We return a populated instance within a week. The remaining week is staff training (1 hour per module).',
  },
];

function Faq() {
  return (
    <Section eyebrow="Frequently asked" title="Questions schools actually ask.">
      <div
        className="overflow-hidden rounded-xl"
        style={{
          border: '1px solid var(--border-line)',
          background: 'var(--bg-surface)',
        }}
      >
        {FAQ.map((item, i) => (
          <FaqRow key={item.q} item={item} divider={i > 0} />
        ))}
      </div>
    </Section>
  );
}

function FaqRow({ item, divider }: { item: FaqItem; divider: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderTop: divider ? '1px solid var(--border-hair)' : undefined }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-6 py-5 text-left"
        aria-expanded={open}
      >
        <span
          className="pr-8 text-[14.5px] font-medium"
          style={{ color: 'var(--fg-high)', letterSpacing: '-0.012em' }}
        >
          {item.q}
        </span>
        <span
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full"
          style={{
            border: '1px solid var(--border-line)',
            background: open ? 'var(--color-accent-500)' : 'var(--bg-canvas)',
            color: open ? 'var(--fg-on-accent)' : 'var(--fg-mid)',
            transition: 'background 150ms, color 150ms',
          }}
        >
          {open ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
        </span>
      </button>
      {open && (
        <div className="px-6 pb-6">
          <p
            className="max-w-3xl text-[13.5px] leading-relaxed"
            style={{ color: 'var(--fg-mid)' }}
          >
            {item.a}
          </p>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* 9 · Final CTA                                                           */
/* ---------------------------------------------------------------------- */

function FinalCta() {
  return (
    <section
      className="relative isolate overflow-hidden"
      style={{ background: '#0A0A0A', color: '#FAFAFA' }}
    >
      {/* Big ambient mark drifting behind — smaller on mobile. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-40 -top-32 opacity-[0.05] sm:-right-32 sm:opacity-[0.06]"
        style={{ color: '#FAFAFA' }}
      >
        <span className="block sm:hidden">
          <Mark4x4 size={320} accent={{ row: 2, col: 1 }} />
        </span>
        <span className="hidden sm:block">
          <Mark4x4 size={520} accent={{ row: 2, col: 1 }} />
        </span>
      </div>

      <div className="relative mx-auto max-w-6xl px-6 py-24 md:py-28">
        <div className="max-w-2xl">
          <p
            className="font-mono text-[11px] uppercase"
            style={{ color: 'rgba(255,255,255,0.45)', letterSpacing: '0.10em' }}
          >
            One last thing
          </p>
          <h2
            className="mt-4"
            style={{
              fontSize: 'clamp(2rem, 4.5vw, 3.25rem)',
              lineHeight: 1.05,
              fontWeight: 600,
              letterSpacing: '-0.035em',
              color: '#FAFAFA',
            }}
          >
            Schools deserve calm software.
          </h2>
          <p
            className="mt-5 max-w-xl text-[15.5px] leading-relaxed"
            style={{ color: 'rgba(255,255,255,0.65)' }}
          >
            Spend thirty minutes with us. We&apos;ll show you the office of a school
            already running on Edupra — and let you decide whether yours could look the
            same way.
          </p>

          <div className="mt-9 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-[13.5px] font-medium"
              style={{
                background: 'var(--color-accent-500)',
                color: 'var(--fg-on-accent)',
                letterSpacing: '-0.005em',
              }}
            >
              Get a demo
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <a
              href={APP_URL}
              className="inline-flex items-center gap-2 text-[13.5px] font-medium"
              style={{ color: 'rgba(255,255,255,0.85)' }}
            >
              Sign in to your school
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
